import type { JSX } from "react";
import { memo, useEffect, useMemo, useState } from "react";
import type { RowData, RowSelectionState } from "@tanstack/react-table";
import { getCoreRowModel, useReactTable } from "@tanstack/react-table";
import clsx from "clsx";

import styles from "./styles.module.scss";
import { Thead } from "./thead/thead";
import { Tbody } from "./tbody/tbody";
import type { ITableProps } from "./types";

import { TableNoData } from "@components/atoms/tableNoData";

/**
 * TODO:add font and all typography
 */

export const Table = memo(function Table<TData extends RowData>(
  props: ITableProps<TData>,
) {
  const {
    columnDef,
    dataJSON,
    thead = true,
    showRow = true,
    hasActionCell = false,
    tableClassName,
    tableContainerClassName,
    onRowSelectionChange,
  } = props;
  const memoizedColumns = useMemo(() => columnDef, [columnDef]);
  const memoizedData = useMemo(() => dataJSON, [dataJSON]);

  const [selectedRows, setSelectedRows] = useState<RowSelectionState>({});

  const tableInstance = useReactTable<TData>({
    columns: memoizedColumns,
    data: memoizedData,
    getCoreRowModel: getCoreRowModel(),
    onRowSelectionChange: (state) => {
      setSelectedRows(state);
    },
    state: {
      rowSelection: selectedRows,
    },
  });

  const dataLength = useMemo(() => memoizedData.length, [memoizedData.length]);
  const rows = tableInstance.getCoreRowModel().rows;
  const headerGroups = tableInstance?.getHeaderGroups();

  const memoizedHeaderGroups = useMemo(() => headerGroups, [headerGroups]);

  useEffect(() => {
    const rows = tableInstance.getSelectedRowModel().rows;

    onRowSelectionChange?.(rows);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [selectedRows]);

  const headComponent = useMemo(
    () =>
      thead &&
      dataLength > 0 && (
        <Thead headerGroups={memoizedHeaderGroups} showRow={showRow} />
      ),
    [dataLength, memoizedHeaderGroups, showRow, thead],
  );

  const bodyComponent = useMemo(
    () =>
      dataLength > 0 ? (
        <Tbody rows={rows} hasActionCell={hasActionCell} showRow={showRow} />
      ) : (
        <tbody>
          <tr>
            <td>
              <TableNoData />
            </td>
          </tr>
        </tbody>
      ),
    [dataLength, hasActionCell, rows, showRow],
  );

  return (
    <div className={clsx([styles["table-container"], tableContainerClassName])}>
      <table className={clsx([styles.table, tableClassName])}>
        {headComponent}
        {bodyComponent}
      </table>
    </div>
  );
}) as <TData extends RowData>(props: ITableProps<TData>) => JSX.Element;
